<script setup>
// S16 — Durable sync phase display.
// Renders the server phase/status for the durable run. The run type selects the
// applicable server-contract track; progress itself is never inferred locally.
// Survives refresh:
// the parent re-fetches the run by id on mount and this component simply
// reflects `run.phase`. Shows per-phase counts, per-site readiness and redacted
// errors. It NEVER shows credentials.
import { computed } from "vue";
import {
  phaseTrackForRunType,
  isTerminalPhase,
} from "@/utils/helpers/shopify";

const props = defineProps({
  run: { type: Object, default: null },
});

const track = computed(() => phaseTrackForRunType(props.run?.run_type));
const currentPhase = computed(() => props.run?.phase || null);
const currentIndex = computed(() => track.value.findIndex(({ phase }) => phase === currentPhase.value));
const failed = computed(() => props.run?.status === "failed" || currentPhase.value === "failed");
const partial = computed(() => props.run?.status === "partial");
const stale = computed(() => currentPhase.value === "stale");
const currentLabel = computed(() =>
  props.run?.status === "queued"
    ? "Queued"
    : track.value.find(({ phase }) => phase === currentPhase.value)?.label || currentPhase.value || props.run?.status || "Unknown"
);

function phaseState(phase) {
  const i = track.value.findIndex((entry) => entry.phase === phase);
  if (currentIndex.value < 0) return failed.value ? "blocked" : "todo";
  if (failed.value && i > currentIndex.value) return "blocked";
  if (i < currentIndex.value || currentPhase.value === "ready") return "done";
  if (i === currentIndex.value) return failed.value ? "failed" : "active";
  return "todo";
}

const perSite = computed(() => props.run?.per_site || []);
const errorSummary = computed(() => props.run?.error_summary || null);
const terminal = computed(() => isTerminalPhase(currentPhase.value) || failed.value || partial.value);

defineExpose({ currentPhase, failed, partial, stale, terminal, phaseState });
</script>

<template>
  <div class="sync-phases" data-test="sync-phases">
    <div class="sync-phases__header">
      <span
        class="sync-phases__status"
        :class="{
          'is-failed': failed,
          'is-partial': partial,
          'is-stale': stale,
          'is-ready': currentPhase === 'ready' && !failed,
        }"
        data-test="run-status"
      >
        <template v-if="failed">Failed</template>
        <template v-else-if="partial">Partially ready</template>
        <template v-else-if="stale">Stale</template>
        <template v-else>{{ currentLabel }}</template>
      </span>
      <span v-if="run && run.scoped_job_id" class="sync-phases__job" data-test="scoped-job-id">
        Run {{ run.attempt || 1 }} · {{ run.run_type || "sync" }}
      </span>
    </div>

    <ol class="sync-phases__track">
      <li
        v-for="step in track"
        :key="step.phase"
        class="sync-phase"
        :class="`sync-phase--${phaseState(step.phase)}`"
        :data-test="`phase-${step.phase}`"
        :data-state="phaseState(step.phase)"
      >
        <span class="sync-phase__dot"></span>
        <span class="sync-phase__label">{{ step.label }}</span>
      </li>
    </ol>

    <div v-if="run && run.counts" class="sync-counts" data-test="run-counts">
      <span>Processed {{ run.counts.processed || 0 }}</span>
      <span>Succeeded {{ run.counts.succeeded || 0 }}</span>
      <span>Quarantined {{ run.counts.quarantined || 0 }}</span>
      <span>Deleted {{ run.counts.deleted || 0 }}</span>
    </div>

    <div v-if="perSite.length" class="sync-persite" data-test="per-site">
      <h4>Per-site readiness</h4>
      <table>
        <tbody>
          <tr v-for="s in perSite" :key="s.ravo_site_id" :data-test="`persite-${s.ravo_site_id}`">
            <td>{{ s.site_name || s.ravo_site_id }}</td>
            <td>
              <span class="persite-badge" :class="`persite-badge--${s.status}`">
                {{ s.phase }} / {{ s.status }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="errorSummary" class="sync-error" role="alert" data-test="run-error">
      <h4>Errors (redacted)</h4>
      <pre>{{ typeof errorSummary === "string" ? errorSummary : JSON.stringify(errorSummary, null, 2) }}</pre>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sync-phases__header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.sync-phases__status {
  font-weight: 700; padding: 3px 10px; border-radius: 100px; font-size: 12px;
  background: var(--primary-50); color: var(--primary-700);
}
.sync-phases__status.is-failed { background: var(--red-50, #fef2f2); color: var(--red-700, #b91c1c); }
.sync-phases__status.is-partial { background: var(--amber-100, #fef3c7); color: var(--amber-700, #b45309); }
.sync-phases__status.is-stale { background: var(--neutral-100); color: var(--neutral-600); }
.sync-phases__status.is-ready { background: var(--green-50, #f0fdf4); color: var(--green-700, #15803d); }
.sync-phases__job { font-size: 11px; color: var(--neutral-500); }
.sync-phases__track {
  display: flex; list-style: none; padding: 0; margin: 0 0 12px; gap: 4px; flex-wrap: wrap;
}
.sync-phase { display: flex; align-items: center; gap: 6px; padding: 4px 10px; flex: 1; min-width: 90px; }
.sync-phase__dot {
  width: 10px; height: 10px; border-radius: 50%; background: var(--neutral-300); flex: none;
}
.sync-phase__label { font-size: 12px; color: var(--neutral-500); }
.sync-phase--done .sync-phase__dot { background: var(--green-500, #22c55e); }
.sync-phase--active .sync-phase__dot { background: var(--primary-500); box-shadow: 0 0 0 3px var(--primary-100, #dbeafe); }
.sync-phase--active .sync-phase__label { color: var(--neutral-900); font-weight: 600; }
.sync-phase--failed .sync-phase__dot { background: var(--red-500, #ef4444); }
.sync-phase--blocked .sync-phase__dot { background: var(--neutral-200); }
.sync-counts { display: flex; gap: 16px; font-size: 12px; color: var(--neutral-600); margin-bottom: 12px; }
.sync-persite table { width: 100%; font-size: var(--text-sm); }
.sync-persite td { padding: 4px 8px; border-bottom: 1px solid var(--neutral-200); }
.persite-badge { font-size: 11px; padding: 1px 8px; border-radius: 100px; background: var(--neutral-100); }
.persite-badge--succeeded, .persite-badge--ready { background: var(--green-50, #f0fdf4); color: var(--green-700, #15803d); }
.persite-badge--failed { background: var(--red-50, #fef2f2); color: var(--red-700, #b91c1c); }
.sync-error { background: var(--red-50, #fef2f2); border-radius: 8px; padding: 8px 12px; }
.sync-error pre { white-space: pre-wrap; word-break: break-word; font-size: 11px; margin: 4px 0 0; }
</style>
